import React from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";

import { GAME_BOX_ICONS } from "@/app/constants.mjs";
import type { GameSymbol } from "@/app/games.mjs";
import ArrowRight from "@/inline-assets/arrow-right2.svg";
import appendUtm from "@/marketing/append-utm.mjs";
import AnchorCard from "@/shared/AnchorCard.jsx";
import Ripple from "@/shared/Ripple.jsx";
import { classNames } from "@/util/class-names.mjs";

// Show "New" tag if it was created within the last 4 weeks
const NEW_DATE_CUTOFF = 1000 * 60 * 60 * 24 * 7 * 4;

function isNewArticle(createdAt?: Date) {
  if (!createdAt) return false;

  return Date.now() < createdAt.getTime() + NEW_DATE_CUTOFF;
}

export type ArticleTileProps = {
  createdAt?: Date;

  background?: string;
  image?: string;

  game?: GameSymbol;
  title: Translation | string;
  subtitle: Translation | string;

  link?: {
    href: string;
    target?: string;
    text?: Translation;
  };

  className?: string;

  children?: React.ReactNode;
};

export default function ArticleTile({
  createdAt,
  background,
  image,
  game,
  title,
  subtitle,
  link,
  className,
  children,
}: ArticleTileProps) {
  const { t } = useTranslation();

  const href = link?.href
    ? appendUtm(link.href, {
        utm_source: "blitz",
        utm_medium: "featured_tile",
        utm_campaign: "landing",
      })
    : undefined;

  const Tag = href ? "a" : "div";

  return (
    <AnchorCard>
      <ArticleContainer
        {...classNames(className)}
        style={image && { minHeight: "370px" }}
      >
        <Tag
          className="body-block focusable card"
          href={href}
          // Add a tab index so it can be focused by tab navigation even if there is no link
          tabIndex={!href ? 0 : undefined}
          draggable={href ? false : undefined}
        >
          {background && (
            <div className="bg">
              <img src={background} role="presentation" alt="" />
              {image && (
                <img src={image} role="presentation" alt="" className="img" />
              )}
            </div>
          )}

          <div className="text-block">
            <h3 className="type-article-headline">
              <span>
                {typeof title === "string" ? title : t(...title)}
                {isNewArticle(createdAt) === true && (
                  <span className="tag type-caption--semi">
                    {t("common:new", "New")}
                  </span>
                )}
              </span>

              {game &&
                React.createElement(GAME_BOX_ICONS[game], {
                  className: "original-colors",
                })}
            </h3>

            <p className="type-body2">
              {typeof subtitle === "string" ? subtitle : t(...subtitle)}
            </p>

            {link && link.text && (
              <div className="link">
                <span className="type-form--button">{t(...link.text)}</span>

                <ArrowRight width={12} />
              </div>
            )}
          </div>

          {href && <Ripple />}
        </Tag>

        {children}
      </ArticleContainer>
    </AnchorCard>
  );
}

const ArticleContainer = styled<
  "article",
  CSSVariables<{
    "--parallax"?: string;
  }>
>("article")`
  --parallax: 10px;
  width: 100%;
  height: auto;
  aspect-ratio: 164 / 187;
  background-color: var(--shade7);
  border-radius: var(--br-lg);
  overflow: hidden;

  .card {
    border-radius: var(--br-lg);
  }

  .body-block {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    height: 100%;
    user-select: none;
    user-drag: none;

    & > .bg {
      position: absolute;
      inset: 0;
      border-radius: var(--br-lg);
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: bottom;
      }
    }

    & > .bg .img {
      position: absolute;
      bottom: 0;
    }

    & > svg {
      width: var(--sp-6);
      height: var(--sp-6);
    }

    & > div.text-block {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--sp-3);

      padding: var(--sp-6);

      transform: translateZ(var(--parallax));

      pointer-events: none;

      h3 {
        display: flex;
        justify-content: space-between;

        width: 100%;

        color: var(--shade0);

        span.tag {
          margin-left: var(--sp-2);
          padding: var(--sp-0_5) var(--sp-1_5);

          color: var(--turq);

          background-color: var(--turq-15);
          border-radius: var(--br);
        }
      }

      svg,
      h3 > svg {
        width: var(--sp-5);
        height: var(--sp-5);
        flex-shrink: 0;
        margin-top: var(--sp-1);
      }

      p {
        color: var(--shade1);
      }

      a,
      button {
        padding: 0;
      }

      & > .link {
        display: flex;
        align-items: center;
        gap: var(--sp-2);

        color: var(--shade2);
      }
    }
  }
`;

export const ArticleBody = styled("div")`
  padding: var(--sp-6);

  transform: translateZ(var(--parallax));
`;

export const ArticleListContainer = styled(ArticleBody)`
  position: absolute;
  inset: 0;
  top: unset;

  ul {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);

    li {
      display: flex;
      flex-direction: column;

      pointer-events: auto;

      a {
        display: flex;
      }
    }
  }
`;
